import { call, fork, put, race, take, select } from 'redux-saga/effects';
import { takeEvery } from 'redux-saga';
import { errorAction } from 'components/utility/actions';
import browserHistory from 'components/utility/history';
import * as restartActions from 'components/widgets/Restart/actions';
import EndpointsClient from 'rest/EndpointsClient';
import Request from 'rest/Request';
import * as actions from './actions';

export const endpointsClient = new EndpointsClient();
const apiClient = new Request();

export function redirectToEndpoint(endpointId) {
	browserHistory.push(`${window.$conf.prefix}/project/apidocs/${endpointId}`);
}

export function redirectToAPIDocs() {
	browserHistory.push(`${window.$conf.prefix}/project/apidocs/`);
}

export function doTestAPIEndpoint(action, cancelToken, apikey, apiPrefixSecurity) {
	const request = action.payload.request;
	let auth;
	const headers = { ...request.params.header };

	// apply auth for apikey and basic
	if (apiPrefixSecurity === 'apikey') {
		headers.apikey = apikey;
	} else if (apiPrefixSecurity === 'basic') {
		auth = {
			username: apikey
		};
	}

	if (request.contentType) {
		headers['Content-Type'] = request.contentType;
	}
	if (request.accept) {
		headers.Accept = request.accept;
	}

	apiClient.validateStatus = () => {
		// all status codes are valid when using testAPI
		return true;
	};

	return apiClient.invoke(
		request.verb,
		request.uri,
		request.params.body,
		null,
		auth,
		headers,
		cancelToken
	);
}

export function *fetchEndpoint(action) {
	try {
		const results = yield call([
			endpointsClient,
			endpointsClient.getEndpoints
		], action.payload.endpointId);

		const {
			endpoint, type, name, triggerTypeId, triggeredFlows, invalidTriggers, url
		} = results.data;

		const endpointId = action.payload.endpointId;
		const payload = {
			endpointId,
			endpoint,
			type,
			name,
			triggerTypeId,
			triggeredFlows,
			invalidTriggers,
			url
		};

		if (type === 'endpoint') {
			// For endpoints we also need to fetch the generated/dereferenced/stripped
			// OpenAPI document as well as the original "endpoint" file that we have
			const response = yield call([ endpointsClient, endpointsClient.getSwagger ],
				endpointId);
			payload.swagger = response.data;
		}

		yield put({
			payload,
			type: actions.FETCH_ENDPOINT_SUCCESS
		});
	} catch (error) {
		const err = errorAction(actions.FETCH_ENDPOINT_ERROR, error, action);
		err.payload.endpointId = action.payload.endpointId;
		yield put(err);
	}
}

/**
 * generator for deleting and endpoint. The action's payload requires
 * the the following: [endpointId, endpointToDelete.method, endpointToDelete.path]
 */
export function *deleteEndpoint(action) {
	let endpointId = null;
	let method = null;
	let endpointPath = null;
	endpointId = action && action.payload && action.payload.endpointId;
	method = action && action.payload && action.payload.endpointToDelete
		&& action.payload.endpointToDelete.method;
	endpointPath = action && action.payload && action.payload.endpointToDelete
		&& action.payload.endpointToDelete.path;

	if (!action.payload || !endpointId || !method || !endpointPath) {
		yield put(errorAction(actions.DELETE_ENDPOINT_ERROR, {}, action));
		return;
	}

	// fire off the rest call and wait for reload

	const deletePayload = {
		endpointId: action.payload.endpointId,
		data: {
			method: action.payload.endpointToDelete.method.toLowerCase(),
			path: action.payload.endpointToDelete.path
		}
	};

	try {
		yield call([ endpointsClient, endpointsClient.deleteEndpoint ], deletePayload);
		const [ result ] = yield [
			race({
				success: take(restartActions.AWAIT_RESTART_SUCCESS),
				error: take(restartActions.AWAIT_RESTART_ERROR),
				timeout: take(restartActions.AWAIT_RESTART_TIMEOUT)
			}),
			put(restartActions.awaitRestart())
		];

		// if its successful close the dialog, and get new endpoints state
		if (result && result.success) {
			yield [
				put({ type: actions.FETCH_ENDPOINT, payload: action.payload }),
				put({ type: actions.DELETE_ENDPOINT_SUCCESS, payload: action.payload })
			];
		} else if (result && result.error) {
			yield put(errorAction(actions.DELETE_ENDPOINT_ERROR, result.error, action));
		}
	} catch (error) {
		// show error message if there was a problem
		yield put(errorAction(actions.DELETE_ENDPOINT_ERROR, error, action));
	}
}

export function *testAPIEndpoint(action) {
	try {
		// Only supporting API key, basic and no authentication.
		const { apikey, apiPrefixSecurity } = yield select(
			state => state.project && state.project.project && state.project.project
		);

		let cancelReq;
		const cancelToken = (c) => {
			cancelReq = c;
		};
		const { response, cancel } = yield race({
			response: call(doTestAPIEndpoint, action, cancelToken, apikey, apiPrefixSecurity),
			cancel: take((cancelAction) => (
				!!(cancelAction.type === actions.TEST_API_ENDPOINT_CANCEL && cancelAction.payload
					&& cancelAction.payload.endpointId && cancelAction.payload.methodId
					&& cancelAction.payload.endpointId === action.payload.endpointId
					&& cancelAction.payload.methodId === action.payload.methodId
				))
			)
		});

		if (cancel) {
			cancelReq && cancelReq();
		} else if (response) {
			yield put({
				type: actions.TEST_API_ENDPOINT_SUCCESS,
				payload: {
					endpointId: action.payload.endpointId,
					methodId: action.payload.methodId,
					response
				}
			});
		}
	} catch (error) {
		yield put(errorAction(actions.TEST_API_ENDPOINT_ERROR, error, action));
	}
}

export function *fetchApiSpecTypes(action) {
	try {
		const response = yield call([ endpointsClient, endpointsClient.getApiSpecTypes ]);
		yield put({
			payload: response.data,
			type: actions.FETCH_API_SPEC_TYPES_SUCCESS
		});
	} catch (error) {
		const err = errorAction(actions.FETCH_API_SPEC_TYPES_ERROR, error, action);
		yield put(err);
	}
}

export function *deleteSpec(action) {
	const { type } = action.payload;

	try {
		yield call([ endpointsClient, endpointsClient.deleteSpec ], type);
	} catch (error) {
		const err = errorAction(actions.DELETE_SPEC_ERROR, error, action);
		yield put(err);
		return;
	}

	// Start waiting for the restart success and show the restart modal.
	yield put(restartActions.awaitRestart());
	// Don't worry about AWAIT_RESTART failures here. These actions are handled elsewhere and will
	// update the restart modal. We will only continue this action if the restart "flow" succeeds.
	yield take(restartActions.AWAIT_RESTART_SUCCESS);

	yield put({
		type: actions.DELETE_SPEC_COMPLETE
	});
}

export function *completeSpecDelete() {
	yield call(redirectToAPIDocs);
}

export function *fetchGroupSummaries(action) {
	try {
		const results = yield call([ endpointsClient, endpointsClient.getGroupSummaries ]);
		yield put({
			payload: results.data,
			type: actions.FETCH_GROUP_SUMMARIES_SUCCESS
		});
	} catch (error) {
		yield put(errorAction(actions.FETCH_GROUP_SUMMARIES_ERROR, error, action));
	}
}

export function *validateSwagger(action) {
	try {
		const response = yield call([
			endpointsClient,
			endpointsClient.validateSwagger
		], action.payload);
		yield put({ type: actions.VALIDATE_SWAGGER_SUCCESS, payload: response.data });
	} catch (error) {
		yield put(errorAction(actions.VALIDATE_SWAGGER_ERROR, error, action));
	}
}

export function *saveEndpoint(action) {
	let endpointId;
	try {
		const response = yield call([ endpointsClient, endpointsClient.create ], action.payload);
		endpointId = response.headers && response.headers.location;
		yield put({ type: actions.SAVE_ENDPOINT_SUCCESS });
	} catch (error) {
		yield put(errorAction(actions.SAVE_ENDPOINT_ERROR, error, action));
		return;
	}

	// Wait for the restart & redirect
	const [ result ] = yield [
		race({
			success: take(restartActions.AWAIT_RESTART_SUCCESS),
			error: take(restartActions.AWAIT_RESTART_ERROR),
			timeout: take(restartActions.AWAIT_RESTART_TIMEOUT)
		}),
		put(restartActions.awaitRestart())
	];

	if (result && result.success) {
		yield call(redirectToEndpoint, endpointId);
	}
}

export default function *sagas() {
	yield fork(takeEvery, actions.FETCH_GROUP_SUMMARIES, fetchGroupSummaries);
	yield fork(takeEvery, actions.VALIDATE_SWAGGER, validateSwagger);
	yield fork(takeEvery, actions.SAVE_ENDPOINT, saveEndpoint);
	yield fork(takeEvery, actions.FETCH_ENDPOINT, fetchEndpoint);
	yield fork(takeEvery, actions.DELETE_ENDPOINT, deleteEndpoint);
	yield fork(takeEvery, actions.TEST_API_ENDPOINT, testAPIEndpoint);
	yield fork(takeEvery, actions.FETCH_API_SPEC_TYPES, fetchApiSpecTypes);
	yield fork(takeEvery, actions.DELETE_SPEC, deleteSpec);
	yield fork(takeEvery, actions.DELETE_SPEC_COMPLETE, completeSpecDelete);
}
